export const commonFields = [
  'LOCATION', 'ADRESSE', 'STANDORT', 'LAGE', 'ORT',
  'CONSTRUCTION YEAR', 'BAUJAHR', 'ERRICHTUNGSJAHR',
  'TYPE OF USE', 'NUTZUNGSART', 'NUTZUNG', 'VERWENDUNG',
  'PLOT AREA', 'GRUNDSTÜCKSFLÄCHE', 'GRUNDFLÄCHE',
  'GROSS FLOOR AREA', 'BRUTTOGRUNDFLÄCHE', 'BGF',
  'RENTAL AREA', 'MIETFLÄCHE',
  'TOTAL FLOORS', 'STOCKWERKE INSG', 'GESAMTSTOCKWERKE',
  'FLOORS ABOVE GROUND', 'STOCKWERKE OBERIRDISCH', 'OBERIRDISCHE STOCKWERKE',
  'FLOORS UNDERGROUND', 'STOCKWERKE UNTERIRDISCH', 'UNTERIRDISCHE STOCKWERKE',
  'TOTAL CAR PARKING SPACES', 'GESAMTE ANZAHL PARKPLÄTZE', 'PARKPLÄTZE GESAMT',
];

export const specialFieldsByType = {
  Wohnen: [
    'NUMBER OF FLATS', 'ANZAHL DER WOHNUNGEN', 'ANZAHL WOHNUNGEN', 'WOHNUNGSANZAHL',
  ],
  Logistik: [
    'FLOOR LOAD CAPACITY', 'TRAGFÄHIGKEIT BODEN', 'BODENBELASTUNG',
    'CLEAR HEIGHT', 'LICHTE HÖHE', 'DECKENHÖHE', 'RAUMHÖHE',
  ],
  Hotel: [
    'NUMBER OF GUESTROOMS', 'ANZAHL GÄSTEZIMMER', 'ZIMMERANZAHL', 'GÄSTERÄUME',
  ],
  Büro: [
    'PARKING SPACES WITH E-CHARGING STATION', 'STELLPLÄTZE MIT E-LADESTATION', 'E-LADESTATION',
  ],
};

export const assetTypeMapping = {
  Logistic: 'Logistik',
  Logistics: 'Logistik',
  Residential: 'Wohnen',
  Hotel: 'Hotel',
  Office: 'Büro',
  Büro: 'Büro',
};

export const propertyMatchesKeywords = (property, keywords) => {
  return keywords.some(
    (keyword) =>
      property.nodeName?.toUpperCase().includes(keyword.toUpperCase()) ||
      property.labelTranslation?.some((label) =>
        label.title?.toUpperCase().includes(keyword.toUpperCase())
      )
  );
};

export const mapCanonicalType = (rawType) => {
  if (!rawType) {
    return null;
  }
  return assetTypeMapping[rawType] || rawType;
};

export const isShortNameProperty = (property) => {
  return (
    property.nodeName?.toUpperCase().includes('SHORT NAME') ||
    property.nodeName?.toUpperCase().includes('KURZBEZEICHNUNG') ||
    property.nodeName?.toUpperCase().includes('KURZE BEZEICHNUNG') ||
    property.labelTranslation?.some(
      (label) =>
        label.title?.toUpperCase().includes('SHORT NAME') ||
        label.title?.toUpperCase().includes('KURZBEZEICHNUNG') ||
        label.title?.toUpperCase().includes('KURZE BEZEICHNUNG')
    )
  );
};

export const isCustomProperty = (property) =>
  property?.custom === true && !property?.deleted && !property?.hidden;

export const resolveSubAssetTypes = ({ properties }) => {
  const types = new Set();

  if (!Array.isArray(properties)) {
    return types;
  }

  properties.forEach((property) => {
    property.nodeValue?.forEach((node) => {
      if (!node?.subAsset) {
        return;
      }
      const raw =
        node.subAsset.subAssetType ?? node.subAsset.type ?? node.subAsset.value;
      const canonical = mapCanonicalType(raw);
      if (canonical) {
        types.add(canonical);
      }
    });
  });

  return types;
};

const collectSpecialFieldKeywords = (subAssetTypes) => {
  const types =
    subAssetTypes instanceof Set ? subAssetTypes : new Set(subAssetTypes);
  const keywords = [];

  types.forEach((subAssetType) => {
    if (specialFieldsByType[subAssetType]) {
      keywords.push(...specialFieldsByType[subAssetType]);
    }
  });

  return keywords;
};

/** Report payload uses nodeValue[0].value; page API may use a scalar nodeValue. */
export const getPropertyRawValue = (property) => {
  const nv = property?.nodeValue;
  if (Array.isArray(nv)) {
    return nv[0]?.value;
  }
  if (nv !== null && nv !== undefined && typeof nv === 'object' && 'value' in nv) {
    return nv.value;
  }
  if (typeof nv === 'string' || typeof nv === 'number') {
    return nv;
  }
  return undefined;
};

export const hasNonEmptyValue = (property) => {
  const raw = getPropertyRawValue(property);
  if (raw === null || raw === undefined) {
    return false;
  }
  const str = String(raw).trim();
  return str !== '';
};

export const matchesAnyTypeRegistry = (property) => {
  return Object.values(specialFieldsByType).some((keywords) =>
    propertyMatchesKeywords(property, keywords)
  );
};

export const shouldShowProperty = (property, subAssetTypes) => {
  if (isShortNameProperty(property)) {
    return false;
  }

  if (isCustomProperty(property)) {
    return true;
  }

  if (propertyMatchesKeywords(property, commonFields)) {
    return true;
  }

  const allSpecialFields = collectSpecialFieldKeywords(subAssetTypes);
  if (
    allSpecialFields.length > 0 &&
    propertyMatchesKeywords(property, allSpecialFields)
  ) {
    return true;
  }

  if (hasNonEmptyValue(property) && !matchesAnyTypeRegistry(property)) {
    return true;
  }

  return false;
};

export const getFilteredProperties = (properties, subAssetTypes) => {
  if (!properties?.length) {
    return properties ?? [];
  }

  return properties.filter((property) =>
    shouldShowProperty(property, subAssetTypes)
  );
};

export const getApplicableTypesForProperty = (property) => {
  return Object.keys(specialFieldsByType).filter((assetType) =>
    propertyMatchesKeywords(property, specialFieldsByType[assetType])
  );
};

export const nodeMatchesSubAssetTypes = (node, applicableTypes) => {
  const rawType =
    node.subAsset.subAssetType ?? node.subAsset.type ?? node.subAsset.value;
  const subAssetType = mapCanonicalType(rawType);

  const name = node.subAsset.name?.toUpperCase();
  const matchesByName =
    name &&
    ((name.includes('LOGISTIC') && applicableTypes.includes('Logistik')) ||
      (name.includes('RESIDENTIAL') && applicableTypes.includes('Wohnen')) ||
      (name.includes('WOHNEN') && applicableTypes.includes('Wohnen')) ||
      (name.includes('HOTEL') && applicableTypes.includes('Hotel')) ||
      (name.includes('OFFICE') && applicableTypes.includes('Büro')) ||
      (name.includes('BÜRO') && applicableTypes.includes('Büro')));

  return applicableTypes.includes(subAssetType) || matchesByName;
};
